import { useEffect, useState, type FormEvent } from 'react';
import { api, type Me, type Project } from '../api.js';
import { href, navigate } from '../router.js';

export function Projects({ me }: { me: Me }) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [title, setTitle] = useState('');
  const [timeWindow, setTimeWindow] = useState('');
  const [error, setError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    api<{ projects: Project[] }>('GET', '/api/projects').then((r) => setProjects(r.projects), (e) => setError(e.message));
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      const { project } = await api<{ project: Project }>('POST', '/api/projects', { title, timeWindow });
      navigate(href.project(project.id));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function signOut() {
    await api('POST', '/api/auth/logout');
    location.reload();
  }

  return (
    <main className="projects">
      <header className="topbar">
        <h1>Projects</h1>
        <span className="who">{me.name}</span>
        <a href={href.settings()}>Settings</a>
        <button className="link" onClick={() => setShowPasswordForm((v) => !v)}>Change password</button>
        <button className="link" onClick={signOut}>Sign out</button>
      </header>
      {showPasswordForm && <ChangePassword onClose={() => setShowPasswordForm(false)} />}
      <form className="new-project" onSubmit={create}>
        <input id="new-title" placeholder="Trip or theme, e.g. Evia, November" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <input id="new-window" placeholder="When (optional)" value={timeWindow} onChange={(e) => setTimeWindow(e.target.value)} />
        <button type="submit">New project</button>
      </form>
      {error && <p className="error" role="alert">{error}</p>}
      {projects === null ? (
        <p>Loading…</p>
      ) : projects.length === 0 ? (
        <p className="empty">No projects yet. Start one above.</p>
      ) : (
        <ul className="project-list">
          {projects.map((p) => (
            <li key={p.id}>
              <a href={href.project(p.id)}>
                <strong>{p.title}</strong>
                {p.timeWindow && <span> · {p.timeWindow}</span>}
                <small>{p.members.map((m) => m.name).join(', ')}</small>
              </a>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function ChangePassword({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    try {
      await api('POST', '/api/auth/password', { currentPassword, newPassword });
      setDone(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="change-password">
      {done ? (
        <p role="status">Password changed.</p>
      ) : (
        <form onSubmit={submit}>
          <label>
            Current password
            <input type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
          </label>
          <label>
            New password
            <input type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
          </label>
          <label>
            Confirm new password
            <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
          </label>
          {error && <p className="error" role="alert">{error}</p>}
          <div className="actions">
            <button type="submit">Change password</button>
            <button type="button" className="link" onClick={onClose}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
